// src/pages/finance-portal/ic-netting/IcNettingDetail.js
//
// Netting run detail with compute/approve/settle actions.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, IconButton, Card, Chip, Alert, Skeleton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalculateIcon from '@mui/icons-material/Calculate';
import CheckIcon from '@mui/icons-material/Check';
import PaymentsIcon from '@mui/icons-material/Payments';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const STATUS_COLORS = {
    DRAFT:     { bg: '#f1f5f9', color: '#64748b' },
    COMPUTED:  { bg: '#fef9c3', color: '#854d0e' },
    APPROVED:  { bg: '#dbeafe', color: '#1d4ed8' },
    SETTLED:   { bg: '#dcfce7', color: '#166534' },
    CANCELLED: { bg: '#fecaca', color: '#991b1b' },
};

const LINE_STATUS_COLORS = {
    PENDING: { bg: '#fef9c3', color: '#854d0e' },
    SETTLED: { bg: '#dcfce7', color: '#166534' },
    SKIPPED: { bg: '#f1f5f9', color: '#94a3b8' },
};

function fmt(val, currency = 'USD', decimals = 2) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency, maximumFractionDigits: decimals,
    }).format(val || 0);
}

export default function IcNettingDetail() {
    const { runId } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [run, setRun] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [settleDialog, setSettleDialog] = useState({
        open: false, kind: null, line: null, settlement_reference: '', notes: '',
    });

    const fetchRun = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/ic-netting/runs/${runId}`);
            if (res.data.success) setRun(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [runId]);

    useEffect(() => { fetchRun(); }, [fetchRun]);

    const action = async (path, body) => {
        try {
            const res = await axiosInstance.post(
                `/api/finance/ic-netting/runs/${runId}/${path}`, body || {});
            enqueueSnackbar(res.data?.message || 'Done', { variant: 'success' });
            fetchRun();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const deleteRun = async () => {
        if (!window.confirm('Delete this run? Cannot be undone.')) return;
        try {
            await axiosInstance.delete(`/api/finance/ic-netting/runs/${runId}`);
            enqueueSnackbar('Deleted', { variant: 'success' });
            navigate('/finance-portal/ic-netting');
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const submitSettleAll = async () => {
        await action('settle', {
            settlement_reference: settleDialog.settlement_reference || null,
        });
        setSettleDialog({ ...settleDialog, open: false });
    };

    const submitSettleLine = async () => {
        try {
            const res = await axiosInstance.post(
                `/api/finance/ic-netting/runs/${runId}/lines/${settleDialog.line.id}/settle`,
                {
                    settlement_reference: settleDialog.settlement_reference || null,
                    notes: settleDialog.notes || null,
                },
            );
            enqueueSnackbar(res.data?.message || 'Line settled', { variant: 'success' });
            setSettleDialog({ ...settleDialog, open: false });
            fetchRun();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const labelCell = { fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b',
                        textTransform: 'uppercase', bgcolor: '#f8fafc',
                        borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' };
    const dataCell = { fontFamily, fontSize: '0.85rem', color: '#475569' };

    if (loading) return <Box sx={{ p: 4, fontFamily }}><Skeleton height={400} /></Box>;
    if (!run) return (
        <Box sx={{ p: 4, fontFamily }}>
            <Alert severity="error">{error || 'Run not found'}</Alert>
            <Button onClick={() => navigate('/finance-portal/ic-netting')} sx={{ mt: 2, fontFamily }}>
                Back
            </Button>
        </Box>
    );

    const sStyle = STATUS_COLORS[run.status] || STATUS_COLORS.DRAFT;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate('/finance-portal/ic-netting')}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                            {run.run_number} · {run.frequency}
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily, fontWeight: 800, color: 'white' }}>
                            {run.name}
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem', mt: 0.25 }}>
                            {run.period_start} → {run.period_end}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ mt: 1 }}>
                    <Chip label={run.status} size="small"
                          sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                bgcolor: sStyle.bg, color: sStyle.color }} />
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1300, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* KPIs */}
                <Box sx={{ display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                          gap: 2, mb: 3 }}>
                    {[
                        { label: 'Pairs', value: run.line_count ?? 0, color: '#0f172a' },
                        { label: 'Total Gross', value: fmt(run.total_gross, run.base_currency, 0),
                          color: '#0f172a' },
                        { label: 'Total Net', value: fmt(run.total_net, run.base_currency, 0),
                          color: '#dc2626' },
                        { label: 'Reduction',
                          value: run.savings_pct != null
                              ? `${(run.savings_pct * 100).toFixed(2)}%` : '—',
                          color: '#16a34a' },
                    ].map(k => (
                        <Card key={k.label} sx={{ borderRadius: '14px', boxShadow: 'none',
                                                   border: '1px solid #e2e8f0', p: 2.5 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8',
                                              textTransform: 'uppercase', fontWeight: 600 }}>
                                {k.label}
                            </Typography>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.3rem',
                                              color: k.color, mt: 0.25 }}>
                                {k.value}
                            </Typography>
                        </Card>
                    ))}
                </Box>

                {/* Action bar */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {(run.status === 'DRAFT' || run.status === 'COMPUTED') && (
                        <Tooltip title="Aggregate IC txns and rebuild the netting lines">
                            <span>
                                <Button startIcon={<CalculateIcon />} variant="contained" size="small"
                                        onClick={() => action('compute')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                              borderRadius: '10px', bgcolor: '#22c55e' }}>
                                    Compute
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {run.status === 'COMPUTED' && (
                        <Button startIcon={<CheckIcon />} variant="outlined" size="small"
                                onClick={() => action('approve')}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                            Approve
                        </Button>
                    )}
                    {run.status === 'APPROVED' && (
                        <Button startIcon={<PaymentsIcon />} variant="contained" size="small"
                                onClick={() => setSettleDialog({
                                    open: true, kind: 'all', line: null,
                                    settlement_reference: '', notes: '',
                                })}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                      borderRadius: '10px', bgcolor: '#3b82f6' }}>
                            Settle All
                        </Button>
                    )}
                    {run.status !== 'SETTLED' && (
                        <Button startIcon={<DeleteIcon />} variant="text" size="small" onClick={deleteRun}
                                sx={{ fontFamily, textTransform: 'none', color: '#94a3b8' }}>
                            Delete
                        </Button>
                    )}
                </Box>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem' }}>
                            Netting Lines ({run.lines?.length || 0})
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={labelCell}>Payer Entity</TableCell>
                                    <TableCell sx={labelCell}>Payee Entity</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Gross Payable</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Gross Receivable</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Net</TableCell>
                                    <TableCell sx={labelCell}>Currency</TableCell>
                                    <TableCell sx={labelCell}>Txns</TableCell>
                                    <TableCell sx={labelCell}>Status</TableCell>
                                    <TableCell sx={labelCell}>Reference</TableCell>
                                    <TableCell sx={labelCell}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(run.lines || []).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4,
                                            ...dataCell, color: '#94a3b8' }}>
                                            No lines yet — click "Compute" to aggregate IC transactions.
                                        </TableCell>
                                    </TableRow>
                                ) : run.lines.map(l => {
                                    const ls = LINE_STATUS_COLORS[l.status] || LINE_STATUS_COLORS.PENDING;
                                    return (
                                        <TableRow key={l.id} hover>
                                            <TableCell sx={dataCell}>
                                                {l.payer_entity_name || `Entity #${l.payer_entity_id}`}
                                            </TableCell>
                                            <TableCell sx={dataCell}>
                                                {l.payee_entity_name || `Entity #${l.payee_entity_id}`}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                                {fmt(l.gross_payable, l.currency)}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                                {fmt(l.gross_receivable, l.currency)}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, textAlign: 'right',
                                                              fontWeight: 700, color: '#1e293b' }}>
                                                {fmt(l.net_amount, l.currency)}
                                            </TableCell>
                                            <TableCell sx={dataCell}>{l.currency}</TableCell>
                                            <TableCell sx={dataCell}>{l.txn_count}</TableCell>
                                            <TableCell>
                                                <Chip label={l.status} size="small"
                                                      sx={{ fontFamily, fontSize: '0.7rem', height: 20,
                                                            bgcolor: ls.bg, color: ls.color }} />
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                {l.settlement_reference || '—'}
                                            </TableCell>
                                            <TableCell>
                                                {l.status === 'PENDING' && run.status === 'APPROVED' && (
                                                    <Button size="small" startIcon={<PaymentsIcon />}
                                                            onClick={() => setSettleDialog({
                                                                open: true, kind: 'line', line: l,
                                                                settlement_reference: '', notes: '',
                                                            })}
                                                            sx={{ fontFamily, textTransform: 'none', fontSize: '0.78rem' }}>
                                                        Settle
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            {/* Settle dialog (used for both single line and bulk) */}
            <Dialog open={settleDialog.open}
                    onClose={() => setSettleDialog({ ...settleDialog, open: false })}
                    PaperProps={{ sx: { borderRadius: '16px' } }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {settleDialog.kind === 'all' ? 'Settle All Pending Lines' : 'Settle Line'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                        <TextField label="Settlement Reference" size="small"
                                   placeholder="e.g. wire #, Stripe Treasury OPID"
                                   value={settleDialog.settlement_reference}
                                   onChange={e => setSettleDialog(p => ({ ...p, settlement_reference: e.target.value }))}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        {settleDialog.kind === 'line' && (
                            <TextField label="Notes" size="small" multiline rows={2}
                                       value={settleDialog.notes}
                                       onChange={e => setSettleDialog(p => ({ ...p, notes: e.target.value }))}
                                       InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                       InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setSettleDialog({ ...settleDialog, open: false })}
                            sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained"
                            onClick={settleDialog.kind === 'all' ? submitSettleAll : submitSettleLine}
                            sx={{ fontFamily, textTransform: 'none', bgcolor: '#3b82f6',
                                  '&:hover': { bgcolor: '#2563eb' } }}>
                        Settle
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
